import {
  SEARCH_GROUP_ORDER,
  type SearchDocumentKind,
  type SearchHit,
  type SearchResponse,
} from '@palaccount/contracts';

/**
 * WHERE A RESULT GOES — §4.3.2's "Enter … Opens the highlighted result", and
 * "Clicking a row … Opens that record's screen".
 *
 * Every path here is one `router.tsx` already routes, and every one is inside
 * the menu group whose area gated the row on the server. That is not a
 * coincidence to be relied on quietly, so it is stated: a row only reaches
 * this map if `SearchService` decided the caller may open its record type, and
 * `route-permissions.ts` then gates the destination independently when the
 * navigation lands. Neither check is the other's backstop — BR-PERM-51 is
 * explicit that a route "must never rely on its menu entry being hidden", and
 * a search result is exactly the "arrived some other way" case that rule is
 * written about.
 */
export function routeFor(hit: SearchHit): string {
  switch (hit.type) {
    case 'item':
      return `/inventory/items/${hit.id}`;
    case 'partner':
      return `/partners/${hit.id}`;
    case 'document':
      return DOCUMENT_ROUTES[hit.kind](hit.id);
    /**
     * FIN-01 is a TREE, not a row-per-page screen: there is no
     * `/accounting/chart/:accountId` to open, and inventing one would be
     * inventing a screen. The chart is where an account is looked at, so the
     * chart is where the row goes.
     */
    case 'account':
      return '/accounting/chart';
    /** HR-01 is likewise one list screen; the employee record opens inside it. */
    case 'employee':
      return '/employees';
  }
}

const DOCUMENT_ROUTES: Record<SearchDocumentKind, (id: string) => string> = {
  quotation: (id) => `/sales/quotes/${id}`,
  sales_order: (id) => `/sales/orders/${id}/edit`,
  invoice: (id) => `/sales/${id}`,
  delivery_note: (id) => `/sales/deliveries/${id}`,
  credit_note: (id) => `/sales/returns/${id}`,
  purchase_order: (id) => `/purchases/orders/${id}`,
  goods_receipt: (id) => `/purchases/receipts/${id}`,
  purchase_bill: (id) => `/purchases/bills/${id}`,
  purchase_return: (id) => `/purchases/returns/${id}`,
  receipt: (id) => `/accounting/receipts/${id}`,
  payment_voucher: (id) => `/accounting/payments/${id}`,
};

/** The uppercase group heading of §4.3.2, as an i18n key. */
export const GROUP_LABEL_KEYS: Record<keyof SearchResponse, string> = {
  items: 'search.groupItems',
  partners: 'search.groupPartners',
  documents: 'search.groupDocuments',
  accounts: 'search.groupAccounts',
  employees: 'search.groupEmployees',
};

/** The document type's own name, which is the first half of its primary line. */
export const DOCUMENT_LABEL_KEYS: Record<SearchDocumentKind, string> = {
  quotation: 'search.docQuotation',
  sales_order: 'search.docSalesOrder',
  invoice: 'search.docInvoice',
  delivery_note: 'search.docDeliveryNote',
  credit_note: 'search.docCreditNote',
  purchase_order: 'search.docPurchaseOrder',
  goods_receipt: 'search.docGoodsReceipt',
  purchase_bill: 'search.docPurchaseBill',
  purchase_return: 'search.docPurchaseReturn',
  receipt: 'search.docReceipt',
  payment_voucher: 'search.docPaymentVoucher',
};

/**
 * The groups present in a response, in §4.3.2's order — *"Groups appear in the
 * order above"* — skipping the ones the role may not open, which arrive as
 * absent keys rather than empty groups (GEN-12a).
 *
 * An OPEN group that simply matched nothing is skipped here too: §4.3.2's
 * "Nothing found" state is about the whole panel, and a heading with a zero
 * beside it is not one of the four states that section lists.
 */
export function presentGroups(
  data: SearchResponse | undefined,
): { key: keyof SearchResponse; total: number; rows: SearchHit[] }[] {
  if (data === undefined) return [];
  return SEARCH_GROUP_ORDER.flatMap((key) => {
    const group = data[key];
    if (group === undefined || group.rows.length === 0) return [];
    return [{ key, total: group.total, rows: group.rows }];
  });
}

/** Every row across every group, in the order they are drawn — the keyboard walk. */
export function flatten(data: SearchResponse | undefined): SearchHit[] {
  return presentGroups(data).flatMap((group) => group.rows);
}

/** The total across the groups the caller can see, for "See all N results". */
export function totalAcross(data: SearchResponse | undefined): number {
  return presentGroups(data).reduce((sum, group) => sum + group.total, 0);
}
